import { getColorFromProgress, sortJobsByHeader } from './../utils/jobUtils';
import { atom, atomFamily, DefaultValue } from 'recoil';
import { selector, selectorFamily } from './recoilSelectors';
import { isDone, Job, STATUS_ARCHIVED } from '../types/job';
import { dayState, pageState } from './appState';
import { PAGE } from '../constants/pages';
import { filterJobsBySelection, jobFilterContext, jobFiltersState } from './jobFiltersState';
import { activeFilterKeys } from '../utils/filters';
import { jobPassesFilters } from '../utils/jobFilters';
import { boardMemberUidsState, boardShowAllUnassignedState } from "./boardState";
import { Day, Uid, UidDay, UidNumber } from "../types/core";
import { coordinatesOk, limitTo, searchMatch, sortJobsByNumber } from "../utils/jobUtils";
import { templatesState } from "./templatesState";
import { sequencesState } from "./sequencesState";
import { ALL_JOBS_GRID_DEFAULT_TYPE, ALL_JOBS_GRID_MEMBER_TYPE, MAX_JOBS } from '../constants/jobs';
import { allJobsCustomSortFieldState, allJobsSortDirectionState, allJobsSortState } from './allJobsState';
import { SORT_DIRECTION } from 'baseui/table';
import { atomWithPersistence } from "../utils/persistence";
import { dispatchJobVisibilityRequestState } from "./dispatchJobVisibilityState";
import { matchesDispatchJobVisibility } from "../utils/dispatchJobVisibility";

export const jobState = atomFamily<Job | undefined, string | undefined>({
    key: 'job',
    default: undefined,
});

export const jobIdsState = atom<string[]>({
    key: 'jobs.ids',
    default: [] as string[],
});

export const dangerousJobsState = selector({
    key: 'jobs.all',
    get: ({ get }) => {
        const jobs = get(jobIdsState)
            .map(id => get(jobState(id)))
            .filter(job => !!job) as Job[];
        return jobs
            .filter(job => coordinatesOk(job))
            .filter(job => job.status !== STATUS_ARCHIVED)
    },
});

export const filteredJobIdsState = selector({
    key: 'jobs.all.filtered.ids',
    get: ({ get }) => {
        return get(dangerousJobsState).map((j) => j.id);
    }
});

export const jobsEvenArchivedState = selector({
    key: 'jobs.all.evenArchived',
    get: ({ get }) => {
        const jobs = get(jobIdsState)
            .map(id => get(jobState(id)))
            .filter(job => !!job) as Job[];
        return jobs
            .filter(job => coordinatesOk(job))
    },
});

export const jobsUpdateEventState = atom({ key: 'job.update.event', default: 0 });

export const jobsSearchState = atom({ key: 'jobs.search', default: '' });

export const jobsForSelectedMemberState = selectorFamily<Job[], {day: Day}>({
    key: 'jobs.member.selected',
    get: ({day}) => ({ get }) => {
        const memberUids = get(boardMemberUidsState);
        if (!memberUids || day === 0) {
            return [] as Job[];
        }
        const search = get(jobsSearchState);
        const allJobs = get(jobsByDayState(day));
        const jobs = allJobs
            .filter(job => memberUids.includes(job.receiverUid))
            .filter(job => searchMatch(job, search))
            .sort(sortJobsByNumber);
        // the Jobs bar lives on the dispatch board; its filters take pins off that map only
        return limitTo(MAX_JOBS, get(pageState) === PAGE.DISPATCH ? filterJobsBySelection(get, jobs) : jobs);
    },
});

export const jobsByDayState = selectorFamily<Job[], number>({
    key: 'jobs.day',
    get: (day) =>
        ({ get }) => {
            return get(dangerousJobsState).filter(job => job.day === day);
        },
});

/**
 * The day's jobs bucketed by assignee, computed once.
 *
 * Every member row used to filter the whole day's job list itself, so a list of
 * 500 members over 7,500 jobs did ~3.7M comparisons — and a single job edit
 * invalidates `dangerousJobsState`, so all of it re-ran on every keystroke-sized
 * change. Grouping in one pass makes that O(jobs) instead of O(members × jobs).
 */
export const jobsByDayGroupedByMemberState = selectorFamily<Map<Uid, Job[]>, Day>({
    key: 'jobs.day.grouped.member',
    get: (day) =>
        ({ get }) => {
            const grouped = new Map<Uid, Job[]>();
            if (day === 0) {
                return grouped;
            }
            // Deliberately unfiltered by search: this is also what "does the route
            // still describe this member's day?" is measured against, and a route
            // must not look stale merely because the operator typed in the job
            // search box. Search is applied per member below.
            get(jobsByDayState(day)).forEach(job => {
                if (job.day !== day) {
                    return;
                }
                const bucket = grouped.get(job.receiverUid);
                if (bucket) {
                    bucket.push(job);
                } else {
                    grouped.set(job.receiverUid, [job]);
                }
            });
            return grouped;
        },
});

export const jobsByMemberDayState = selectorFamily<Job[], UidDay>({
    key: 'jobs.member.day',
    get: ({ uid, day }) =>
        ({ get }) => {
            const memberUids = get(boardMemberUidsState);
            if (!memberUids || day === 0) {
                return [] as Job[];
            }
            const mine = get(jobsByDayGroupedByMemberState(day)).get(uid);
            if (!mine?.length) {
                return [] as Job[];
            }
            const search = get(jobsSearchState);
            const sort = get(allJobsSortState(ALL_JOBS_GRID_MEMBER_TYPE));
            const direction = get(allJobsSortDirectionState(ALL_JOBS_GRID_MEMBER_TYPE));
            const customSortField = get(allJobsCustomSortFieldState(ALL_JOBS_GRID_MEMBER_TYPE));
            const sortedJobs = mine
                .filter(job => searchMatch(job, search))
                .sort((j1, j2) => sortJobsByHeader(j1, j2, sort, customSortField));

            return limitTo(MAX_JOBS, direction === SORT_DIRECTION.DESC ? sortedJobs.reverse() : sortedJobs);
        },
});


export const jobIdsByMemberDayState = selectorFamily<string[], UidDay>({
    key: 'jobs.member.day.ids',
    get: (uidDay) =>
        ({ get }) => {
            return get(jobsByMemberDayState(uidDay)).map((j) => j.id);
        },
});

export const jobProgressStepsByMemberDayState = selectorFamily<string[], UidDay>({
    key: 'jobs.member.day.progressSteps',
    get: (uidDay) =>
        ({ get }) => {
            return get(jobsByMemberDayState(uidDay)).map(j => getColorFromProgress(j));
        },
});

export const jobsPercentByMemberDayState = selectorFamily<number, UidDay>({
    key: 'jobs.member.day.percent',
    get: (uidDay) =>
        ({ get }) => {
            const jobs = get(jobsByMemberDayState(uidDay));
            const jobsCount = jobs.length;
            return jobsCount > 0 ? 100 * jobs.filter(j => isDone(j)).length / jobsCount : 0;
        },
});

export const routeJobsNeedsUpdateState = selectorFamily<boolean, UidDay>({
    key: 'jobs.member.route.needsUpdate',
    get: (uidDay) =>
        ({ get }) => {
            const jobs = get(jobsByMemberDayState(uidDay));
            if(!jobs.length) return false;

            const numbers = jobs.map(job => job.number);
            numbers.sort((a, b) => a - b);
            // Check if each number is consecutive
            const areConsecutive = numbers.every((num, index) => {
                if (index === 0) return true;
                return num === numbers[index - 1] + 1;
            });
            return !areConsecutive;
        },
});

export const jobIdsByMemberDispatchState = selectorFamily<string[], UidDay>({
    key: 'jobs.member.dispatch',
    get: (uidDay) => ({ get }) => {
        const jobsByMemberDay = get(jobsByMemberDayState(uidDay));
        return jobsByMemberDay.map((job) => job.id);
    },
    set: (uidDay) => ({ get, set }, newOrderedIds) => {
        if (newOrderedIds instanceof DefaultValue) return;
        const jobs = newOrderedIds.map((id) => get(jobState(id))).filter((job) => !!job) as Job[];
        jobs.forEach((job) =>
            set(jobState(job.id), {
                ...job,
                number: newOrderedIds.findIndex((id) => id === job.id) + 1,
                receiverUid: uidDay.uid,
                day: uidDay.day,
            })
        );
    },
});

export const jobsByMemberOrUnassignedState = selectorFamily<Job[], UidDay>({
    key: 'jobs.member.day',
    get: (uidDay) => ({ get }) => {
        if (uidDay.uid === '') {
            return get(jobsForUnassignedState);
        } else {
            return get(jobsByMemberDayState(uidDay))
        }
    },
});

export const jobsForUnassignedState = selector({
    key: 'jobs.unassigned',
    get: ({ get }) => {
        const search = get(jobsSearchState);
        const showAllUnassigned = get(boardShowAllUnassignedState);
        const day = get(dayState);
        const sort = get(allJobsSortState(ALL_JOBS_GRID_DEFAULT_TYPE));
        const direction = get(allJobsSortDirectionState(ALL_JOBS_GRID_DEFAULT_TYPE));
        const customSortField = get(allJobsCustomSortFieldState(ALL_JOBS_GRID_DEFAULT_TYPE));
        const jobs = get(dangerousJobsState)
            .filter(job => job.receiverUid === '')
            .filter(job => showAllUnassigned || job.day === day)
            .filter(job => searchMatch(job, search))
            .sort((j1, j2) => sortJobsByHeader(j1, j2, sort, customSortField));
        const filtered = filterJobsBySelection(get, jobs);
        return limitTo(MAX_JOBS, direction === SORT_DIRECTION.DESC ? filtered.reverse() : filtered);
    },
});

export const jobsForUnassignedVisibleState = selectorFamily<Job[], boolean>({
    key: "jobs.unassigned.visible",
    get: (applyDispatchTeamVisibility) => ({ get }) => {
        const jobs = get(jobsForUnassignedState);
        if (!applyDispatchTeamVisibility) {
            return jobs;
        }
        const visibility = get(dispatchJobVisibilityRequestState);
        return jobs.filter((job) => matchesDispatchJobVisibility(job, visibility));
    },
});

export const jobIdsForUnassignedState = selector({
    key: 'jobs.unassigned.ids',
    get: ({ get }) => {
        return get(jobsForUnassignedState).map((job) => job.id); 
    },
});

export const jobIdsUnassignedDispatchState = atom<string[]>({
    key: 'jobs.unassiged.dispatch',
    default: selector({
        key: 'jobs.unassiged.dispatch.initializer',
        get: ({ get }) => {
            const unassignedJobs = get(jobsForUnassignedState);
            return unassignedJobs.map((job) => job.id); 
        }
    })
});

export const selectedJobIdsState = atom({
    key: 'jobs.selected.uids',
    default: [] as string[],
});

export const isJobSelectedState = selectorFamily<boolean, string>({
    key: "jobs.selected.isSelected",
    get: (uid) => ({ get }) => {
        const selectedJobIds = get(selectedJobIdsState);
        return selectedJobIds.includes(uid);
    },
});

export const selectedJobsIdsCountState = selector<number>({
    key: "jobs.selected.count",
    get: ({ get }) => {
        const selectedJobIds = get(selectedJobIdsState);
        return selectedJobIds.length;
    },
});

export const selectedJobsState = selector({
    key: 'jobs.selected',
    get: ({ get }) => {
        const ids = get(selectedJobIdsState);
        if (ids.length === 0) {
            return [] as Job[];
        }
        const jobs = get(jobsEvenArchivedState).filter(job => ids.includes(job.id));
        const templates = get(templatesState(true)).filter(job => ids.includes(job.id));
        let selection = [...jobs, ...templates];
        get(sequencesState).forEach(seq => {
            const selSeqJobs = seq.jobs.filter(job => ids.includes(job.id));
            if (selSeqJobs.length > 0) {
                selection = [...selection, ...selSeqJobs];
            }
        });
        return selection;
    }
});

export const jobsAssignedState = selector({
    key: 'jobs.assigned',
    get: ({ get }) => {
        const day = get(dayState);
        return get(jobsByDayState(day))
            .filter(job => !!job.assigneeUsername);
    }
});

export const creatingJobForUidState = atom<UidNumber | undefined>({
    key: 'job.creating.uidnumber',
    default: undefined,
});

export const jobIdsPinnedState = atomWithPersistence<string[]>("jobs.ids.pinned", []);

/** The pinned jobs the search leaves, before the Jobs bar's filters. */
export const pinnedJobsBeforeFiltersState = selector<Job[]>({
    key: 'jobs.pinned.beforefilters',
    get: ({ get }) => {
        const search = get(jobsSearchState);
        const allJobs = get(dangerousJobsState);
        const pinnedIds = get(jobIdsPinnedState);
        const jobs = pinnedIds
            .map(id => allJobs.find(job => job.id === id));
        const filtered = jobs.filter(job => job && searchMatch(job, search));
        return filtered as Job[];
    },
});

/** The Pinned card's jobs: the Jobs bar's filters apply here like on every other list. */
export const pinnedJobsState = selector<Job[]>({
    key: 'jobs.pinned',
    get: ({ get }) => filterJobsBySelection(get, get(pinnedJobsBeforeFiltersState)),
});

export const activeRouteTabState = atomWithPersistence<"list" | "table" | "week">("jobs.route.tab.active", "list");

/**
 * Whether a job passes the Jobs bar's filters. Routes keep every stop in order and dim the ones
 * that don't match, so stop 2 stays stop 2; lists and pins leave them out.
 */
export const jobMatchesJobFiltersState = selectorFamily<boolean, string>({
    key: "job.filters.matches",
    get: (jobId) => ({ get }) => {
        const selection = get(jobFiltersState);
        const keys = activeFilterKeys(selection);
        if (keys.length === 0) {
            return true;
        }
        const job = get(jobState(jobId));
        if (!job) {
            return true;
        }
        return jobPassesFilters(job, selection, jobFilterContext(get, keys));
    },
});
